/* --------------------------------------------------------------------------
 *  CacheService  (Angular / RxJS / Capacitor-aware, Filesystem edition)
 *
 *  • Native (Capacitor) → saves each entry as its own file in
 *    <Cache>/cacheService/…, so there’s effectively no 5 MB ceiling.
 *  • Web browser → falls back to window.localStorage.
 * ------------------------------------------------------------------------ */

import {
  HttpBackend,
  HttpClient,
  HttpHeaders,
  HttpResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, from, of, defer } from "rxjs";
import { switchMap, tap, shareReplay, catchError } from "rxjs/operators";
import { NetworkService } from "./network.service";
import * as LZString from "lz-string";

import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";

/* ────────────────────────────── interfaces ─────────────────────────────── */
interface CachedResource {
  data: any;
  contentType: string;
}

interface StorageEngine {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  keys(): Promise<string[]>;
  forceRefresh?(): Promise<void>;
}

/* ─────────────────────────────── service ───────────────────────────────── */
@Injectable({ providedIn: "root" })
export class CacheService {
  private readonly cacheName = "resource-cache";
  private readonly storage: StorageEngine & {
    forceRefresh?: () => Promise<void>;
  };
  private readonly directHttp: HttpClient;

  private readonly isNative = Capacitor.getPlatform() !== "web";
  private readonly basePath = "cacheService"; // root folder (native only)

  // ───────────────────── in-flight dedupe maps ─────────────────────
  private inFlightRequests = new Map<string, Observable<any>>();
  private inFlightRawPromises = new Map<string, Promise<any>>();
  private inFlightItemsPromises = new Map<string, Promise<any[]>>();

  constructor(
    private http: HttpClient,
    httpBackend: HttpBackend,
    private network: NetworkService
  ) {
    this.directHttp = new HttpClient(httpBackend);
    this.storage = this.buildStorageEngine();
  }

  private fromFile = (f: string) => decodeURIComponent(f.replace(/\.txt$/, ""));

  /* ───────────────────────── storage selector ──────────────────────────── */
  private buildStorageEngine(): StorageEngine & {
    forceRefresh?: () => Promise<void>;
  } {
    if (this.isNative) {
      // Directory.Cache is explicitly OS-reclaimable (both iOS and Android
      // can purge it under storage pressure with no warning), which is
      // exactly wrong for an "offline-first" cache - a purge while offline
      // means the resource can never be re-fetched. auth.service.ts already
      // uses Directory.Data for everything that must actually persist
      // (tokens, userInfo); this cache needs the same guarantee, since a
      // missing icon/feature-description/bundle is the "always
      // re-downloading" symptom this is fixing.
      const dir = Directory.Data;
      const toFile = (k: string) =>
        `${this.basePath}/${encodeURIComponent(k.trim())}.txt`;

      let cachedKeys: string[] = [];
      let isStale = true;

      const refreshDir = async () => {
        try {
          // console.log(
          //   `[CacheService] 🔍 refreshDir() called - about to call Filesystem.readdir`
          // );
          // console.log(
          //   `[CacheService] Path: ${this.basePath}, Directory: Cache`
          // );
          const startTime = Date.now();

          const { files } = await Filesystem.readdir({
            path: this.basePath,
            directory: dir,
          });

          const duration = Date.now() - startTime;
          // console.log(
          //   `[CacheService] ✅ Filesystem.readdir completed in ${duration}ms`
          // );
          // console.log(`[CacheService] Found ${files.length} files`);

          cachedKeys = files.map((f) => this.fromFile(f.name));
          // console.log(
          //   `[CacheService] Mapped to ${cachedKeys.length} cache keys`
          // );
        } catch (e) {
          // console.error("[CacheService] ❌ Cache keys refresh error:", e);
          // console.error("[CacheService] Error details:", {
          //   message: e?.message,
          //   name: e?.name,
          //   stack: e?.stack,
          // });
          cachedKeys = [];
        }
        isStale = false;
        // console.log(
        //   `[CacheService] refreshDir() complete, isStale set to false`
        // );
      };

      return {
        getItem: async (key) => {
          try {
            // console.log(`[CacheService] getItem() called for key: ${key}`);
            const filePath = toFile(key.trim());
            // console.log(`[CacheService] Reading file at path: ${filePath}`);
            const startTime = Date.now();

            const result = await Filesystem.readFile({
              path: filePath,
              directory: dir,
              encoding: Encoding.UTF8,
            });

            const duration = Date.now() - startTime;
            const data = (result as { data: string }).data;
            // console.log(
            //   `[CacheService] getItem() completed in ${duration}ms, data length: ${
            //     data?.length || 0
            //   }`
            // );
            return data;
          } catch (err) {
            // console.error("[CacheService] ❌ Cache get error:", err);
            // console.error("[CacheService] Error details:", {
            //   message: err?.message,
            //   name: err?.name,
            //   code: err?.code,
            // });
            return null;
          }
        },
        setItem: async (key, value) => {
          try {
            await Filesystem.writeFile({
              path: toFile(key.trim()),
              data: value,
              directory: dir,
              encoding: Encoding.UTF8,
              recursive: true,
            });
            isStale = true;
          } catch (e) {
            // console.log("Cache set error:", e);
          }
        },
        removeItem: async (key) => {
          try {
            await Filesystem.deleteFile({
              path: toFile(key.trim()),
              directory: dir,
            });
            isStale = true;
          } catch (e) {
            // console.log("Cache delete error:", e);
          }
        },
        keys: async () => {
          // console.log(`[CacheService] keys() called, isStale: ${isStale}`);
          if (isStale) {
            // console.log(`[CacheService] isStale=true, calling refreshDir()...`);
            await refreshDir();
            // console.log(`[CacheService] refreshDir() returned`);
          } else {
            // console.log(
            //   `[CacheService] isStale=false, returning cached keys (${cachedKeys.length} keys)`
            // );
          }
          return cachedKeys;
        },
        forceRefresh: async () => {
          isStale = true;
          await refreshDir();
        },
      };
    }

    return {
      getItem: async (k) => localStorage.getItem(k.trim()),
      setItem: async (k, v) => localStorage.setItem(k.trim(), v),
      removeItem: async (k) => localStorage.removeItem(k.trim()),
      keys: async () => Object.keys(localStorage),
    };
  }

  /* ───────────────────── helper: unique cache key ────────────────────── */
  private cacheKey(url: string) {
    return `${this.cacheName}-${url.trim()}`;
  }

  appendCacheBustingUrl(url: string): string {
    return `${url}${url.includes("?") ? "&" : "?"}_=${Date.now()}`;
  }

  async doesKeyExist(key: string): Promise<boolean> {
    // console.log(`[CacheService] doesKeyExist() called for key: ${key}`);
    const fullKey = this.cacheKey(key.trim());
    // console.log(`[CacheService] Full cache key: ${fullKey}`);
    // console.log(`[CacheService] Getting all keys...`);
    const keys = await this.storage.keys();
    // console.log(`[CacheService] Total keys: ${keys.length}`);
    const exists = keys.includes(fullKey);
    // console.log(`[CacheService] Key exists: ${exists}`);
    return exists;
  }

  async doesRawKeyExist(key: string): Promise<boolean> {
    const keys = await this.storage.keys();
    return keys.includes(key);
  }

  /* ───────────────────────── shared getResource core ───────────────────────── */
  private getResourceShared(
    url: string,
    refresh: boolean,
    cache: boolean
  ): Observable<any> {
    const execId =
      new Date().getTime() + "-" + Math.floor(Math.random() * 1000);

    const key = this.cacheKey(url) + "-" + refresh;

    if (this.inFlightRequests.has(key)) {
      return this.inFlightRequests.get(key)!;
    }

    const request$ = defer(async () => {
      const exists = await this.doesKeyExist(url);

      if (!exists) return { cached: null, cacheExisted: false };

      const raw = await this.storage.getItem(this.cacheKey(url));

      if (!raw) return { cached: null, cacheExisted: false };

      try {
        const parsed = JSON.parse(
          LZString.decompressFromUTF16(raw)
        ) as CachedResource;
        // console.log("Now we refresh: " + refresh + " [execId: " + execId + "]");
        // Background refresh disabled to prevent cache confusion during upgrades
        // if (refresh) {
        //   // console.log(
        //   //   "CacheService refreshing cache for " +
        //   //     url +
        //   //     " [execId: " +
        //   //     execId +
        //   //     "]"
        //   // );
        //   this.updateCacheInBackground(url);
        // }
        return { cached: this.wrap(parsed), cacheExisted: true };
      } catch (e) {
        // console.warn(
        //   `Bad cache entry for ${url} [execId: ${execId}], refetching…`,
        //   e
        // );
        await this.clearResource(url);
        return { cached: null, cacheExisted: false };
      }
    }).pipe(
      switchMap((result) => {
        if (result.cached) return of(result.cached);

        // console.log(
        //   "Cache not found for " + url + ", fetching… [execId: " + execId + "]"
        // );
        // Only write to cache if cache didn't exist before (initial download)
        // This prevents silent cache updates when loading features
        const shouldCache = cache && !result.cacheExisted;
        return this.fetchAndCache(url, shouldCache);
      }),
      tap({
        finalize: () => this.inFlightRequests.delete(key),
        error: () => this.inFlightRequests.delete(key),
      }),
      shareReplay(1)
    );

    this.inFlightRequests.set(key, request$);
    return request$;
  }

  /* ───────────────────── public API ────────────────────────────── */
  getResource(url: string, cache = false): Observable<any> {
    return this.getResourceShared(url, cache, cache);
  }

  getResourceAndOnlyCacheIfNative(url: string): Observable<any> {
    return this.getResourceShared(url, this.isNative, this.isNative);
  }

  getResourceWithoutRefresh(url: string, cache = false): Observable<any> {
    return this.getResourceShared(url, false, cache);
  }

  /* ───────────── fetch resource without caching (for version checks) ────────── */
  getResourceWithoutCaching(url: string): Observable<any> {
    // Add timestamp to URL for stronger cache-busting
    const timestamp = Date.now();
    const separator = url.includes("?") ? "&" : "?";
    const fullUrl = `${url}${separator}_t=${timestamp}&_nocache=1`;
    const client = url.includes("s3.amazonaws") ? this.directHttp : this.http;

    return client
      .get(fullUrl, {
        headers: new HttpHeaders({
          "Cache-Control": "no-cache, no-store, must-revalidate",
        }),
        responseType: "blob",
        observe: "response",
      })
      .pipe(
        switchMap((res: HttpResponse<Blob>) => this.handleResponse(res)),
        switchMap(async (parsed) => this.wrap(parsed)),
        catchError((err) => {
          if (this.network.isNetworkError(err)) this.network.markInternetDown();
          // console.error(`Fetch failed for ${url}`, err);
          return of(null);
        })
      );
  }

  /* ─────────────────── low-level: HTTP + store result ──────────────────── */
  private fetchAndCache(url: string, cache: boolean): Observable<any> {
    const fullUrl = this.appendCacheBustingUrl(url);
    const client = url.includes("s3.amazonaws") ? this.directHttp : this.http;

    return client
      .get(fullUrl, {
        headers: new HttpHeaders({ "Cache-Control": "no-cache" }),
        responseType: "blob",
        observe: "response",
      })
      .pipe(
        switchMap((res: HttpResponse<Blob>) => this.handleResponse(res)),
        switchMap(async (parsed) => {
          if (cache && parsed) {
            try {
              const compressed = LZString.compressToUTF16(
                JSON.stringify(parsed)
              );
              await this.storage.setItem(this.cacheKey(url), compressed);
            } catch (e) {
              // console.error(`Failed to store cache for ${url}`, e);
            }
          }
          return this.wrap(parsed);
        }),
        catchError((err) => {
          if (this.network.isNetworkError(err)) this.network.markInternetDown();
          // console.error(`Fetch failed for ${url}`, err);
          return of(null);
        })
      );
  }

  /* ───────────────────── response → CachedResource ─────────────────────── */
  private handleResponse(
    response: HttpResponse<Blob>
  ): Observable<CachedResource> {
    const ct = response.headers.get("Content-Type") || "";

    if (ct === "application/json") {
      return from(
        response.body!.text().then((txt) => ({
          data: JSON.parse(txt),
          contentType: ct,
        }))
      );
    }

    if (ct.startsWith("text/") || ct === "image/svg+xml") {
      return from(
        response.body!.text().then((txt) => ({ data: txt, contentType: ct }))
      );
    }

    if (ct === "image/png" || ct === "image/jpeg") {
      return from(
        this.blobToBase64(response.body!).then((b64) => ({
          data: b64,
          contentType: ct,
        }))
      );
    }

    return from(
      response.body!.text().then((txt) => ({ data: txt, contentType: ct }))
    );
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  }

  private wrap(p: CachedResource) {
    return { data: p.data, contentType: p.contentType };
  }

  /* ─────────────── background refresh (fire-and-forget) ────────────────── */
  private updateCacheInBackground(url: string) {
    this.fetchAndCache(url, true).subscribe();
  }

  /* ─────────────────────────── cache helpers ───────────────────────────── */
  async clearResource(url: string) {
    await this.storage.removeItem(this.cacheKey(url));
  }

  async clearResourcesByPrefix(urlPrefix: string) {
    const keys = await this.storage.keys();
    const prefix = this.cacheKey(urlPrefix);
    await Promise.all(
      keys
        .filter((k) => k.startsWith(prefix))
        .map((k) => this.storage.removeItem(k))
    );
  }

  async clearAllResources() {
    const keys = await this.storage.keys();
    await Promise.all(
      keys
        .filter((k) => k.startsWith(this.cacheName))
        .map((k) => this.storage.removeItem(k))
    );
  }

  async clearAllCachedData() {
    const keys = await this.storage.keys();
    // Clear all keys that are not biometric credentials
    await Promise.all(
      keys
        .filter(
          (k) =>
            !k.startsWith("biometric_") &&
            k !== "authToken" &&
            k !== "userInfo" &&
            k !== "userRoles" &&
            k !== "userOrganization"
        )
        .map((k) => this.storage.removeItem(k))
    );
  }

  async isResourceCached(url: string) {
    return (await this.storage.getItem(this.cacheKey(url))) !== null;
  }

  /* ─────────── arbitrary JSON helpers ───────────────────────────── */
  async setCachedData(key: string, value: any) {
    try {
      const compressed = LZString.compressToUTF16(JSON.stringify(value));
      await this.storage.setItem(key, compressed);
    } catch (e) {
      // console.error(`Failed to cache data for ${key}`, e);
    }
  }

  async removeCachedData(key: string) {
    try {
      await this.storage.removeItem(key);
    } catch (e) {
      // console.error(`Failed to remove cached data for ${key}`, e);
    }
  }

  async getCachedData(key: string): Promise<any | null> {
    const startTime = performance.now();
    console.log(`[CACHE-PERF] getCachedData START for key: ${key}`);

    if (this.inFlightRawPromises.has(key)) {
      console.log(`[CACHE-PERF] Returning in-flight promise for key: ${key}`);
      return this.inFlightRawPromises.get(key)!;
    }

    const promise = (async () => {
      // Skip doesRawKeyExist check - it's inefficient (fetches all keys)
      // Just try getItem directly - it returns null if key doesn't exist
      const getItemStart = performance.now();
      const raw = await this.storage.getItem(key);
      const getItemDuration = performance.now() - getItemStart;
      console.log(
        `[CACHE-PERF] storage.getItem took ${getItemDuration.toFixed(
          2
        )}ms for key: ${key}`
      );

      if (!raw) {
        console.log(`[CACHE-PERF] No cached data found for key: ${key}`);
        return null;
      }

      try {
        const decompressStart = performance.now();
        const result = JSON.parse(LZString.decompressFromUTF16(raw));
        const decompressDuration = performance.now() - decompressStart;
        console.log(
          `[CACHE-PERF] Decompress+parse took ${decompressDuration.toFixed(
            2
          )}ms for key: ${key}`
        );
        return result;
      } catch {
        console.warn(
          `[CACHE-PERF] Bad cache entry for ${key}, clearing it out`
        );
        await this.storage.removeItem(key);
        return null;
      } finally {
        this.inFlightRawPromises.delete(key);
        const totalDuration = performance.now() - startTime;
        console.log(
          `[CACHE-PERF] getCachedData TOTAL took ${totalDuration.toFixed(
            2
          )}ms for key: ${key}`
        );
      }
    })();

    this.inFlightRawPromises.set(key, promise);
    return promise;
  }

  async getCachedItems(
    filter: (k: string) => boolean,
    forceRefresh = false
  ): Promise<any[]> {
    // console.log(
    //   `[CacheService] getCachedItems() called, forceRefresh: ${forceRefresh}`
    // );
    const filterKey = filter.toString() + (forceRefresh ? "-refresh" : "");
    // console.log(`[CacheService] Filter key: ${filterKey.substring(0, 100)}...`);

    if (this.inFlightItemsPromises.has(filterKey) && !forceRefresh) {
      // console.log(`[CacheService] Returning existing in-flight promise`);
      return this.inFlightItemsPromises.get(filterKey)!;
    }

    // console.log(
    //   `[CacheService] Creating new promise, calling collectCached()...`
    // );
    const promise = (async () => {
      const result = await this.collectCached(filter, forceRefresh);
      // console.log(
      //   `[CacheService] collectCached() returned ${result.length} items`
      // );
      return result;
    })();

    promise.finally(() => {
      // console.log(
      //   `[CacheService] Promise finalized, deleting from inFlightItemsPromises`
      // );
      this.inFlightItemsPromises.delete(filterKey);
    });

    this.inFlightItemsPromises.set(filterKey, promise);
    // console.log(`[CacheService] Returning promise...`);
    return promise;
  }

  /* ───────────────────────── internal helpers ─────────────────────────── */
  private async collectCached(
    predicate: (k: string) => boolean,
    forceRefresh = false
  ): Promise<any[]> {
    // console.log(`[CacheService] collectCached() called`);
    // Force a fresh directory read if requested (native only)
    if (forceRefresh && this.storage.forceRefresh) {
      // console.log(
      //   `[CacheService] forceRefresh requested, calling storage.forceRefresh()`
      // );
      await this.storage.forceRefresh();
      // console.log(`[CacheService] storage.forceRefresh() completed`);
    }

    // console.log(`[CacheService] Getting keys and filtering...`);
    const keys = (await this.storage.keys()).filter(
      (k) => k.startsWith(this.cacheName) && predicate(k)
    );
    // console.log(`[CacheService] Found ${keys.length} matching keys`);
    // console.log(`[CacheService] Keys:`, keys);

    // console.log(
    //   `[CacheService] Starting Promise.all to read ${keys.length} files...`
    // );
    const results = await Promise.all(
      keys.map(async (k, index) => {
        try {
          // console.log(
          //   `[CacheService] [${index + 1}/${keys.length}] Reading file: ${k}`
          // );
          const startTime = Date.now();
          const raw = await this.storage.getItem(k);
          const duration = Date.now() - startTime;
          // console.log(
          //   `[CacheService] [${index + 1}/${
          //     keys.length
          //   }] Read completed in ${duration}ms, size: ${raw?.length || 0} bytes`
          // );

          if (!raw) {
            // console.log(
            //   `[CacheService] [${index + 1}/${
            //     keys.length
            //   }] No data, returning null`
            // );
            return null;
          }

          // console.log(
          //   `[CacheService] [${index + 1}/${
          //     keys.length
          //   }] Decompressing and parsing...`
          // );
          const parsed = JSON.parse(
            LZString.decompressFromUTF16(raw)
          ) as CachedResource;
          // console.log(
          //   `[CacheService] [${index + 1}/${keys.length}] Parse successful`
          // );
          return this.wrap(parsed);
        } catch (e) {
          // console.error(
          //   `[CacheService] [${index + 1}/${keys.length}] ❌ Error reading ${k}:`,
          //   e
          // );
          return null;
        }
      })
    );

    // console.log(`[CacheService] Promise.all completed, filtering results...`);
    const filtered = results.filter(Boolean) as any[];
    // console.log(
    //   `[CacheService] collectCached() returning ${filtered.length} items`
    // );
    return filtered;
  }
}
